import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Capabilities, getAllCapabilities, useCapabilityOverrides } from '@/auth-capabilities';
import { Alert, Button, cn, Switch } from '@/components/ui';
import { auditorMediaQueries } from '@/config';
import { useAuth } from '@/context/appContext';
import { useFeatures } from '@/context/FeatureContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface Props {
  open: boolean;
}

export const CapabilitiesOverrideDrawer = ({ open }: Props) => {
  const { hasAuth } = useAuth();
  const { overrides, setOverrides, clearAllOverrides } = useCapabilityOverrides();
  const flags = useFeatures();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expanded, setExpanded] = useState<Capabilities[]>([]);
  const drawerBleeding = 40;
  const phone = useMediaQuery(auditorMediaQueries.downSm);
  const compact = useMediaQuery(auditorMediaQueries.downLg);
  // Same inset as the page: full width on a phone, drawer width only when the drawer pushes content.
  const leftDisplacement = phone ? '0px' : open && !compact ? 'var(--width-drawer)' : 'var(--width-mini-drawer)';
  const authorized = hasAuth(Capabilities.overrideCapabilities);
  const enabled = flags.auth_capabilities_override;

  // biome-ignore lint/correctness/useExhaustiveDependencies: carried over from the former ESLint suppression
  useEffect(() => {
    if (!enabled) {
      clearAllOverrides();
      setDrawerOpen(false);
    }
    // clearAllOverrides is provided by the auth context and is not referentially stable.
  }, [enabled]);

  if (!enabled) {
    return null;
  }

  if (!authorized) {
    return (
      <div className="fixed left-1/2 top-4 z-tooltip w-full max-w-lg -translate-x-1/2 px-4">
        <Alert tone="caution" role="alert">
          You do not have authorization to override your capabilities
        </Alert>
      </div>
    );
  }

  return (
    <section
      aria-label="Capability overrides"
      className={cn(
        'fixed bottom-0 right-0 z-overlay flex flex-col border border-line bg-canvas shadow-auditor-overlay',
        'transition-slide duration-300 ease-drawer motion-reduce:transition-none',
        drawerOpen ? 'translate-y-0' : 'translate-y-full',
      )}
      style={{
        height: `calc(60% - ${drawerBleeding}px)`,
        left: leftDisplacement,
      }}
    >
      <button
        type="button"
        aria-expanded={drawerOpen}
        onClick={() => setDrawerOpen(current => !current)}
        className={cn(
          'absolute inset-x-0 -top-10 flex h-10 items-center rounded-t-auditor-lg px-4 text-left',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand',
          drawerOpen || !overrides.length ? 'bg-line' : 'bg-critical/25',
        )}
      >
        <span className="absolute left-1/2 top-2 h-1.5 w-8 -translate-x-1/2 rounded-full bg-surface" />
        <strong className="text-sm text-ink">
          {overrides.length ? 'Capability Overrides Enabled' : 'No Capabilities Overridden'}
        </strong>
      </button>

      <div
        className={cn(
          'scrollbar-muted h-full overflow-y-auto overscroll-contain p-4',
          drawerOpen || !overrides.length ? 'bg-line' : 'bg-critical/25',
        )}
      >
        <Button variant="secondary" onClick={clearAllOverrides}>
          Clear All Overrides
        </Button>
        <div className="mt-3 grid gap-2">
          {Object.entries(getAllCapabilities()).map(([key, value]) => {
            const capability = key as Capabilities;
            const isExpanded = expanded.includes(capability);
            const checked = overrides.find(item => item.capability === capability)?.enabled ?? hasAuth(capability);

            return (
              <div key={capability} className="overflow-hidden rounded-auditor border border-line bg-surface">
                <div className="flex items-center gap-3 p-3">
                  <Switch
                    checked={checked}
                    disabled={capability === Capabilities.overrideCapabilities}
                    aria-label={`Override ${capability}`}
                    onCheckedChange={nextChecked => setOverrides(capability, nextChecked)}
                  />
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand"
                    onClick={() =>
                      setExpanded(current =>
                        isExpanded ? current.filter(item => item !== capability) : [...current, capability],
                      )
                    }
                  >
                    <span className="w-1/3 shrink-0 text-sm font-medium text-ink">{capability}</span>
                    <span className="min-w-0 flex-1 text-sm text-ink-muted">{value.help}</span>
                    {isExpanded ? (
                      <ChevronUp className="size-4 shrink-0 text-ink-muted" />
                    ) : (
                      <ChevronDown className="size-4 shrink-0 text-ink-muted" />
                    )}
                  </button>
                </div>
                {isExpanded && (
                  <div className="border-t border-line bg-canvas px-4 py-3 text-sm text-ink">
                    <p className="m-0">
                      Requires that <i>all</i> of the following conditions are met:
                    </p>
                    <ul className="mb-0 mt-2 list-disc pl-5">
                      {value.checkAll.map(check => (
                        <li key={check.name}>{check.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
